import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import currencyService, { type Currency } from "../services/currency";

type CurrencySaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useCurrency() {
  const { t } = useI18n();
  const currencies = ref<Currency[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedCurrency = ref<Currency | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});

  const form = ref<Currency>({
    code: "",
    name: "",
    symbol: "",
    rate: 1,
  });

  const fetchCurrencies = async () => {
    loading.value = true;
    try {
      currencies.value = await currencyService.getCurrencies();
    } catch (error) {
      console.error("Failed to fetch currencies:", error);
      Swal.fire({
        icon: "error",
        title: t("currency_page.load_failed"),
        text: t("currency_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredCurrencies = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return currencies.value;

    return currencies.value.filter((currency) =>
      [currency.code, currency.name, currency.symbol]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("currency_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("currency_page.items_per_page") },
  }));

  watch(
    filteredCurrencies,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const resetForm = () => {
    form.value = {
      code: "",
      name: "",
      symbol: "",
      rate: 1,
    };
    errors.value = {};
  };

  const openCreateModal = () => {
    selectedCurrency.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (currency: Currency) => {
    selectedCurrency.value = currency;
    errors.value = {};
    form.value = {
      id: currency.id,
      code: currency.code || "",
      name: currency.name || "",
      symbol: currency.symbol || "",
      rate: currency.rate || 1,
      created_by: currency.created_by,
    };
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    errors.value = {};
  };

  const updateField = (field: keyof Currency, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const saveCurrency = async () => {
    loading.value = true;
    errors.value = {};
    try {
      const payload = {
        code: String(form.value.code || "").toUpperCase(),
        name: form.value.name || "",
        symbol: form.value.symbol || "",
        rate: Number(form.value.rate || 0),
      };

      if (selectedCurrency.value?.id) {
        await currencyService.updateCurrency(selectedCurrency.value.id, payload);
      } else {
        await currencyService.createCurrency(payload);
      }

      await fetchCurrencies();
      await Swal.fire({
        icon: "success",
        title: selectedCurrency.value ? t("currency_page.updated_title") : t("currency_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as CurrencySaveError;
      console.error(error);
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        const message =
          requestError.response?.data?.message ||
          Object.values(requestError.response?.data?.errors || {}).flat()[0] ||
          t("currency_page.save_failed_text");
        Swal.fire({
          icon: "error",
          title: t("currency_page.save_failed"),
          text: message as string,
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteCurrency = async (currency: Currency) => {
    if (!currency?.id) return;

    const result = await Swal.fire({
      title: t("currency_page.delete_title"),
      text: t("currency_page.delete_text", { name: currency.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("currency_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("currency_page.deleting_title"),
        text: t("currency_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await currencyService.deleteCurrency(currency.id);
      await fetchCurrencies();

      Swal.fire({
        icon: "success",
        title: t("currency_page.deleted_title"),
        text: t("currency_page.deleted_text", { name: currency.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("currency_page.delete_failed"),
        text: t("currency_page.delete_failed_text"),
      });
    }
  };

  return {
    searchQuery,
    filteredCurrencies,
    showModal,
    selectedCurrency,
    form,
    errors,
    loading,
    paginationConfig,
    fetchCurrencies,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    saveCurrency,
    deleteCurrency,
  };
}
